import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Check, AlertCircle, FileCode } from 'lucide-react';
import theme from '../theme';
import NFTUploader from '../components/NFTUploader';
import AIGenerator from '../components/AIGenerator';
import NFTGallery from '../components/NFTGallery';
import SmartContractWizard from '../components/SmartContractWizard';
import PortfolioTypeSelector from '../components/PortfolioTypeSelector';
import SafeLanguageWarnings from '../components/SafeLanguageWarnings';
import KickbackOfferEvidence from '../components/KickbackOfferEvidence';
import HelpTip from '../components/HelpTip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  'Apparel', 'Art', 'Music', 'Gaming', 'Sports', 'Collectibles',
  'Photography', 'Virtual Worlds', 'Trading Cards', 'Domain Names', 'Other'
];

const EnhancedPortfolioWizard = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get('edit') || null;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [safeWarnings, setSafeWarnings] = useState([]);
  const [pendingPortfolioId, setPendingPortfolioId] = useState(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Concept
    name: '',
    description: '',
    category: '',
    otherCategory: '',
    portfolioTypes: [],

    // P10.6: Product Details (optional)
    showProductDetails: false,
    product_name: '',
    product_image_url: '',
    manufacturer: '',
    origin_country: '',
    patent_numbers: '',
    sku: '',

    // BUG #40 — Kickback Item / Offer Evidence (Step 1, separate from Step 2 artwork)
    offerEvidence: { offer_type: '', fields: {}, uploads: {} },

    // Step 2: NFT Artwork
    nftSource: 'upload', // 'upload', 'ai', 'gallery'
    selectedNFTs: [],
    nftMetadata: null,
    
    // Step 3: Smart Contract
    contract: null,
    
    // Step 4: Management — Batch 1C-B1 FINAL SOURCE-OF-TRUTH: no silent
    // business-decision defaults on a NEW Kickback. Creator must explicitly
    // choose management type, kickback percent, and quantity.
    managementType: null,
    selectedBroker: null,
    kickbackPercent: null,
    nftQuantity: null,
    
    // Step 5: Utilities & Agreement
    utilities: [],
    agreementAccepted: false,
  });

  // BUG #40 — when opening the wizard in edit mode for a saved draft,
  // hydrate offerEvidence + other Step 1 fields so nothing is lost.
  useEffect(() => {
    if (!editingId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/creator/portfolios/${editingId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const p = d?.portfolio;
        if (!p) return;
        setFormData((prev) => ({
          ...prev,
          name: p.name || prev.name,
          description: p.description || prev.description,
          category: p.category || prev.category,
          portfolioTypes: (
            Array.isArray(p.portfolio_type_ids) && p.portfolio_type_ids.length ? p.portfolio_type_ids
            : Array.isArray(p.portfolio_types) && p.portfolio_types.length ? p.portfolio_types
            : (p.portfolio_type ? [p.portfolio_type] : prev.portfolioTypes)
          ),
          product_name: p.product_name || '',
          manufacturer: p.manufacturer || '',
          origin_country: p.origin_country || '',
          sku: p.sku || '',
          patent_numbers: Array.isArray(p.patent_numbers) ? p.patent_numbers.join(', ') : (p.patent_numbers || ''),
          offerEvidence: p.offer_evidence || prev.offerEvidence,
          selectedNFTs: Array.isArray(p.nft_asset_ids) ? p.nft_asset_ids : (prev.selectedNFTs || []),
          contract: p.contract_id ? { contract_id: p.contract_id, existing: true } : prev.contract,
          // Batch 1C-A — hydrate persisted Step 4 management + rewards
          managementType: p.management_type || prev.managementType,
          selectedBroker: p.curator_id || prev.selectedBroker,
          kickbackPercent: p.kickback_percent ?? prev.kickbackPercent,
          nftQuantity: p.quantity ?? prev.nftQuantity,
          unitPrice: p.unit_price ?? prev.unitPrice,
        }));
        // Batch 1C-A correction — Explicit edit destination (`?step=N`)
        // overrides wizard_current_step. Used by Edit Evidence to force
        // Step 1 regardless of the persisted resume position.
        if (Number.isFinite(forcedStep) && forcedStep >= 1 && forcedStep <= 5) {
          setCurrentStep(forcedStep);
        } else {
          const persistedStep = parseInt(p.wizard_current_step, 10);
          if (Number.isFinite(persistedStep) && persistedStep >= 1 && persistedStep <= 5) {
            setCurrentStep(persistedStep);
          }
        }
      })
      .catch(() => { /* ignore — legacy row without new fields */ });
  }, [editingId]);

  // Batch 1C-A — dirty tracker so Exit can prompt "Leave Without Saving?"
  // only when there are unsaved changes since the last persisted save.
  const [dirty, setDirty] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showSavedSuccess, setShowSavedSuccess] = useState(null); // portfolio_id after Save Kickback Setup
  const [showDraftSaved, setShowDraftSaved] = useState(null); // portfolio_id after Save and Finish Later
  const [savingDraft, setSavingDraft] = useState(false);
  // Batch 1C-B1 FINAL — Step-level validation + branded finalize-error UI.
  const [stepErrors, setStepErrors] = useState([]);          // per-step missing-field list
  const [finalizeErrorModal, setFinalizeErrorModal] = useState(null); // {title, body, blockers[]}

  // Batch 1C-A correction — explicit edit destinations (Edit Evidence)
  // override wizard_current_step so the Creator lands on the correct
  // section rather than being pulled back to their last resume position.
  const forcedStep = parseInt(searchParams.get('step'), 10);
  const forcedSection = searchParams.get('section') || null;

  // Batch 1C-A FINAL §5 — evidence deep-link: when the wizard opens
  // with ?section=evidence on Step 1, scroll the evidence section into
  // view once it has rendered. Explicit destination MUST NOT overwrite
  // wizard_current_step (handled above by not persisting on scroll).
  useEffect(() => {
    if (forcedSection !== 'evidence' || currentStep !== 1) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="offer-evidence-section-anchor"]');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 600);
    return () => clearTimeout(t);
  }, [forcedSection, currentStep]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  // Batch 1C-A — Save and Finish Later. Persists whatever is currently
  // in the wizard without requiring downstream-step fields, keeps the
  // Kickback in DRAFT, records the current step so Resume Setup opens
  // the correct step, and never triggers launch / deploy / architect
  // review side effects.
  const buildDraftPayload = () => {
    return {
      portfolio_id: editingId || pendingPortfolioId || null,
      wizard_current_step: currentStep,
      name: formData.name || null,
      description: formData.description || null,
      category: formData.category === 'Other' ? formData.otherCategory : formData.category,
      portfolio_type: formData.portfolioTypes?.[0] || null,
      portfolio_type_ids: formData.portfolioTypes || [],
      product_name: formData.product_name?.trim() || null,
      manufacturer: formData.manufacturer?.trim() || null,
      origin_country: formData.origin_country?.trim() || null,
      sku: formData.sku?.trim() || null,
      patent_numbers: formData.patent_numbers
        ? formData.patent_numbers.split(',').map(s => s.trim()).filter(Boolean)
        : [],
      offer_evidence: formData.offerEvidence || null,
      nft_asset_ids: formData.selectedNFTs || [],
      // Batch 1C-A correction — preserve canonical contract_drafts.id
      // linkage so Save-and-Finish-Later never wipes the generated
      // contract draft on new-portfolio inserts.
      contract_id: formData.contract?.contract_id || null,
      management_type: formData.managementType || null,
      curator_id: formData.selectedBroker || null,
      kickback_percent: formData.kickbackPercent || 0,
      quantity: formData.nftQuantity || 0,
      unit_price: formData.unitPrice || 0,
    };
  };

  const handleSaveAndFinishLater = async () => {
    const token = localStorage.getItem('token');
    if (!token || savingDraft) return;
    setSavingDraft(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/portfolios/save-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(buildDraftPayload()),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Failed to save draft');
        return;
      }
      const data = await resp.json();
      const pid = data.portfolio_id;
      if (pid && !editingId && !pendingPortfolioId) setPendingPortfolioId(pid);
      setDirty(false);
      // Batch 1C-A correction — surface a branded "Draft Saved"
      // confirmation instead of silently routing to the detail page.
      // The Creator can dismiss to stay in the wizard or navigate.
      setShowDraftSaved(pid);
    } catch (e) {
      alert('Network error while saving draft. Your entries are preserved.');
    } finally {
      setSavingDraft(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.description && formData.category && formData.portfolioTypes.length > 0;
      case 2:
        return formData.selectedNFTs.length > 0;
      case 3:
        return formData.contract !== null;
      case 4:
        return formData.kickbackPercent && formData.nftQuantity;
      case 5:
        return formData.agreementAccepted;
      default:
        return false;
    }
  };

  // Batch 1C-B1 FINAL Part 3 — collect the concrete list of missing
  // required fields so the wizard can display an inline, human-readable
  // validation summary instead of blocking silently or throwing an alert.
  const collectMissingForStep = (step) => {
    const missing = [];
    if (step === 1) {
      if (!formData.name?.trim()) missing.push('Kickback Name');
      if (!formData.description?.trim()) missing.push('Description');
      if (!formData.category) missing.push('Category');
      if (!(formData.portfolioTypes || []).length) missing.push('At least one Kickback Type');
    } else if (step === 2) {
      if (!(formData.selectedNFTs || []).length) missing.push('At least one selected Kickback Artwork');
    } else if (step === 3) {
      if (!formData.contract) missing.push('Contract draft (choose a template and generate)');
    } else if (step === 4) {
      if (!formData.kickbackPercent) missing.push('Kickback Percentage');
      if (!formData.nftQuantity) missing.push('Quantity');
    } else if (step === 5) {
      if (!formData.agreementAccepted) missing.push('Kickback submission agreement');
    }
    return missing;
  };

  const handleNext = () => {
    // Batch 1C-B1 FINAL Part 3 — Continue must validate the CURRENT step.
    // Save-and-Finish-Later remains the escape hatch for incomplete state.
    // On Step 5 the server independently validates via /finalize-setup, so
    // we still block-and-summarize here for the final step too.
    const missing = collectMissingForStep(currentStep);
    if (missing.length > 0) {
      setStepErrors(missing);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
      return;
    }
    setStepErrors([]);
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      // Batch 1A correction — if opened in edit mode (?edit=<id>), update
      // the existing Kickback's Step 1 + offer_evidence in place instead of
      // creating a duplicate. Downstream steps (contract, launch state)
      // remain untouched.
      if (editingId) {
        // Batch 1C-A PRE-DEPLOYMENT INVARIANT #3 — lifecycle fields
        // (initial_setup_completed*, agreement_accepted*) are NOT sent
        // in the generic PATCH body. They are server-controlled and
        // only transition via POST /portfolios/{id}/finalize-setup.
        const patch = {
          name: formData.name,
          description: formData.description,
          category: formData.category === 'Other' ? formData.otherCategory : formData.category,
          portfolio_type: formData.portfolioTypes?.[0] || 'Revenue Sharing',
          portfolio_type_ids: formData.portfolioTypes || [],
          product_name: formData.product_name?.trim() || null,
          product_image_url: formData.product_image_url?.trim() || null,
          manufacturer: formData.manufacturer?.trim() || null,
          origin_country: formData.origin_country?.trim() || null,
          patent_numbers: formData.patent_numbers
            ? formData.patent_numbers.split(',').map(s => s.trim()).filter(Boolean)
            : [],
          sku: formData.sku?.trim() || null,
          nft_asset_ids: formData.selectedNFTs || [],
          offer_evidence: formData.offerEvidence || null,
        };
        if (currentStep === 5) {
          patch.wizard_current_step = 5;
        }
        const resp = await fetch(`${BACKEND_URL}/api/portfolios/${editingId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(patch),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          alert(err.detail || 'Failed to save changes');
          return;
        }
        if (currentStep === 5) {
          // Batch 1C-A PRE-DEPLOYMENT INVARIANT #3 — the server-controlled
          // finalize path is the ONLY way to transition
          //   initial_setup_completed=false -> true
          //   agreement_accepted=false -> true
          // with server-owned timestamps. Frontend never sets these itself.
          const finResp = await fetch(`${BACKEND_URL}/api/portfolios/${editingId}/finalize-setup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ agreement_accepted: true }),
          });
          if (!finResp.ok) {
            const err = await finResp.json().catch(() => ({}));
            // Batch 1C-B1 FINAL — map backend finalize errors to a
            // branded modal instead of `window.alert('initial_setup_incomplete')`.
            const detail = err.detail;
            const blockerObjs = (detail && typeof detail === 'object' && Array.isArray(detail.blockers)) ? detail.blockers : [];
            setFinalizeErrorModal({
              title: 'Kickback Setup Needs Attention',
              body: 'Some required setup information is still missing. Review the items below before saving your Kickback setup.',
              blockers: blockerObjs.length
                ? blockerObjs.map(b => b.label || b.missing_reason || b.id).filter(Boolean)
                : [typeof detail === 'string' ? detail : 'Please complete the required setup and try again.'],
            });
            return;
          }
          setDirty(false);
          setShowSavedSuccess(editingId);
        } else {
          navigate(`/creator/portfolios/${editingId}`);
        }
        return;
      }

      // P2.4: Build payload with all required fields
      const payload = {
        name: formData.name || 'Untitled Portfolio',
        description: formData.description || '',
        category: formData.category === 'Other' ? formData.otherCategory : formData.category,
        portfolio_type: formData.portfolioTypes?.[0] || 'Revenue Sharing',
        portfolio_type_ids: formData.portfolioTypes || [],
        nft_asset_ids: formData.selectedNFTs || [],
        contract_id: formData.contract?.contract_id,
        management_type: formData.managementType || 'curator',
        curator_id: formData.selectedBroker,
        kickback_percent: formData.kickbackPercent || 10,
        quantity: formData.nftQuantity || 100,
        unit_price: formData.unitPrice || 0,
        utilities: formData.utilities || [],
        mark_ready: formData.markReady || false,  // P2.4: Flag to set READY_TO_LAUNCH status
        // P10.6: Optional product details (only sent when set)
        product_name: formData.product_name?.trim() || null,
        product_image_url: formData.product_image_url?.trim() || null,
        manufacturer: formData.manufacturer?.trim() || null,
        origin_country: formData.origin_country?.trim() || null,
        patent_numbers: formData.patent_numbers
          ? formData.patent_numbers.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        sku: formData.sku?.trim() || null,
        // BUG #40 — separate Step 1 offer evidence payload; distinct from
        // Step 2 artwork (nft_asset_ids). Backend persists it under
        // portfolios.offer_evidence.
        offer_evidence: formData.offerEvidence || null,
      };

      const response = await fetch(`${BACKEND_URL}/api/portfolios/create-full`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const warnings = data.safe_language_warnings || [];
        const nextId = data.portfolio_id;
        // Batch 1C-A PRE-DEPLOYMENT INVARIANT #3 — even on the new-portfolio
        // create path, initial_setup_completed only transitions via the
        // server-controlled finalize endpoint. The create-full route stores
        // the wizard payload; finalize-setup validates + flips lifecycle.
        if (nextId) {
          try {
            await fetch(`${BACKEND_URL}/api/portfolios/${nextId}/finalize-setup`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ agreement_accepted: true }),
            });
          } catch (_) { /* finalize failure is surfaced via readiness on the detail page */ }
        }
        // Phase 7F — warn-not-block: if warnings returned, surface them before navigating.
        if (warnings.length > 0) {
          setSafeWarnings(warnings);
          setPendingPortfolioId(nextId || null);
        }
        // Batch 1C-A — final save success screen replaces immediate
        // redirect so Creator sees "Kickback Setup Saved" state.
        else if (nextId) {
          setDirty(false);
          setShowSavedSuccess(nextId);
        } else {
          navigate('/creator/portfolios');
        }
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Portfolio creation failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error creating portfolio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8F9FB, #FFFFFF)',
      padding: '20px',
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{
              fontFamily: theme.fonts.heading,
              fontSize: '28px',
              fontWeight: 700,
              color: theme.colors.text.primary,
              marginBottom: '4px'
            }}>
              Create Kickback
            </h1>
            <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
              Step {currentStep} of 5
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {currentStep < 5 && (
              <button
                data-testid="wizard-save-and-finish-later-btn"
                onClick={handleSaveAndFinishLater}
                disabled={savingDraft}
                style={{
                  padding: '8px 14px',
                  background: '#FFFFFF',
                  border: `1px solid ${theme.colors.primary}`,
                  borderRadius: theme.radius.sm,
                  color: theme.colors.primary,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: savingDraft ? 'wait' : 'pointer',
                  opacity: savingDraft ? 0.7 : 1,
                }}
              >
                {savingDraft ? 'Saving…' : 'Save and Finish Later'}
              </button>
            )}
          <button
            data-testid="wizard-exit-btn"
            onClick={() => setShowExitConfirm(true)}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              color: theme.colors.text.secondary,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <X size={16} />
            Exit
          </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          height: '4px',
          background: theme.colors.bg.tertiary,
          borderRadius: '2px',
          marginBottom: '32px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            background: theme.colors.primary,
            width: `${(currentStep / 5) * 100}%`,
            transition: 'width 0.3s'
          }} />
        </div>

        {/* Batch 1C-B1 FINAL Part 3 — inline step validation summary. Never
            uses window.alert. Shown when handleNext blocks on missing fields. */}
        {stepErrors.length > 0 && (
          <div data-testid="step-error-summary" style={{
            padding: '14px 16px', marginBottom: 16,
            background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: theme.radius.md,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <AlertCircle size={16} color="#B91C1C" />
              <strong style={{ fontSize: 14, color: '#7F1D1D' }}>Complete the required information before continuing.</strong>
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#7F1D1D' }}>
              {stepErrors.map((m, i) => <li key={i} data-testid={`step-error-${i}`}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Step Content */}
        <div style={{
          background: theme.colors.bg.card,
          borderRadius: theme.radius.card,
          padding: '40px',
          boxShadow: theme.colors.shadows.lg,
          border: `1px solid ${theme.colors.borders}`,
          minHeight: '500px'
        }}>
          {/* STEP 1: Portfolio Concept & Type */}
          {currentStep === 1 && (
            <div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: theme.colors.text.primary,
                marginBottom: '24px'
              }}>
                Portfolio Concept & Type
              </h2>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  marginBottom: '8px'
                }}>
                  Portfolio Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g., Limited Hoodie Drop, VIP Course Access, Employee Reward Program"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  marginBottom: '8px'
                }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe what this Kickback represents, who it is for, and what value holders may receive."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    fontSize: '14px',
                    color: theme.colors.text.primary,
                    background: '#FFFFFF',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  marginBottom: '12px'
                }}>
                  Category *
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '12px'
                }}>
                  {CATEGORY_OPTIONS.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => updateField('category', category)}
                      style={{
                        padding: '10px 16px',
                        border: `2px solid ${formData.category === category ? theme.colors.primary : theme.colors.borders}`,
                        borderRadius: theme.radius.sm,
                        background: formData.category === category ? `${theme.colors.primary}15` : '#FFFFFF',
                        color: formData.category === category ? theme.colors.primary : theme.colors.text.secondary,
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                {formData.category === 'Other' && (
                  <div style={{ marginTop: '12px' }}>
                    <input
                      type="text"
                      placeholder="Specify your category"
                      value={formData.otherCategory}
                      onChange={(e) => updateField('otherCategory', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${theme.colors.borders}`,
                        borderRadius: theme.radius.sm,
                        fontSize: '14px',
                        color: theme.colors.text.primary,
                        background: '#FFFFFF'
                      }}
                    />
                  </div>
                )}
              </div>

              <PortfolioTypeSelector
                onSelect={(types) => updateField('portfolioTypes', types)}
                allowMultiple={true}
                selectedTypes={formData.portfolioTypes}
              />

              {/* BUG #40 — Kickback Item / Offer Evidence */}
              <div data-testid="offer-evidence-section-anchor">
              <KickbackOfferEvidence
                value={formData.offerEvidence}
                onChange={(ev) => updateField('offerEvidence', ev)}
              />
              </div>

              {/* P10.6: Product Details (optional) */}
              <div data-testid="product-details-section" style={{ marginTop: '24px', borderTop: `1px solid ${theme.colors.borders}`, paddingTop: '20px' }}>
                <button
                  type="button"
                  data-testid="toggle-product-details"
                  onClick={() => updateField('showProductDetails', !formData.showProductDetails)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 0, marginBottom: '12px',
                    fontSize: '14px', fontWeight: 600, color: theme.colors.primary,
                  }}>
                  {formData.showProductDetails ? '−' : '+'} Product Details (optional)
                </button>
                <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: formData.showProductDetails ? '16px' : 0 }}>
                  Add real-product metadata (manufacturer, SKU, patents…) that will be saved with the portfolio.
                </p>
                {formData.showProductDetails && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                    {[
                      { key: 'product_name', label: 'Product Name', placeholder: 'e.g., AeroGlide Sneaker v2' },
                      { key: 'sku', label: 'SKU', placeholder: 'e.g., AGS-V2-001' },
                      { key: 'manufacturer', label: 'Manufacturer', placeholder: 'e.g., NimbusFoot Inc.' },
                      { key: 'origin_country', label: 'Origin Country', placeholder: 'e.g., Italy' },
                      { key: 'patent_numbers', label: 'Patent Numbers (comma-separated)', placeholder: 'e.g., US12345678, EP9876543' },
                      { key: 'product_image_url', label: 'Product Image URL', placeholder: 'https://…' },
                    ].map(f => (
                      <div key={f.key}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '4px' }}>{f.label}</label>
                        <input
                          type="text"
                          data-testid={`product-field-${f.key}`}
                          value={formData[f.key]}
                          onChange={e => updateField(f.key, e.target.value)}
                          placeholder={f.placeholder}
                          style={{
                            width: '100%', padding: '10px',
                            border: `1px solid ${theme.colors.borders}`,
                            borderRadius: theme.radius.sm,
                            fontSize: '13px', color: theme.colors.text.primary,
                            background: '#FFFFFF',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: NFT Artwork */}
          {currentStep === 2 && (
            <div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: theme.colors.text.primary,
                marginBottom: '16px'
              }}>
                Kickback Artwork
              </h2>
              <p style={{
                fontSize: '14px',
                color: theme.colors.text.secondary,
                marginBottom: '24px'
              }}>
                Upload, generate, or select artwork from your gallery
              </p>

              {/* Source Selection */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                  {[
                    { value: 'upload', label: 'Upload Files' },
                    { value: 'ai', label: 'AI Generate' },
                    { value: 'gallery', label: 'From Gallery' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateField('nftSource', option.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: `2px solid ${formData.nftSource === option.value ? theme.colors.primary : theme.colors.borders}`,
                        borderRadius: theme.radius.sm,
                        background: formData.nftSource === option.value ? `${theme.colors.primary}15` : '#FFFFFF',
                        color: formData.nftSource === option.value ? theme.colors.primary : theme.colors.text.secondary,
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {formData.nftSource === 'upload' && (
                  <NFTUploader
                    allowMultiple={true}
                    selectedAssets={formData.selectedNFTs}
                    onUploadComplete={(files) => {
                      const ids = files.map(f => f.file_id);
                      updateField('selectedNFTs', [...formData.selectedNFTs, ...ids]);
                    }}
                  />
                )}

                {formData.nftSource === 'ai' && (
                  <AIGenerator
                    onGenerate={(images) => {
                      const ids = images.map(img => img.file_id);
                      updateField('selectedNFTs', [...formData.selectedNFTs, ...ids]);
                    }}
                  />
                )}

                {formData.nftSource === 'gallery' && (
                  <NFTGallery
                    onSelect={(ids) => updateField('selectedNFTs', ids)}
                    selectedIds={formData.selectedNFTs}
                    allowMultiple={true}
                  />
                )}
              </div>

              {formData.selectedNFTs.length > 0 && (
                <div data-testid="wizard-selected-summary" style={{
                  padding: '16px',
                  background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${theme.colors.borders}`
                }}>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    marginBottom: '8px'
                  }}>
                    Selected for this Kickback: {formData.selectedNFTs.length} image{formData.selectedNFTs.length === 1 ? '' : 's'}
                  </p>
                  <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
                    You can add more from Upload / AI Generate / Gallery, or continue to Step 3.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Smart Contract */}
          {currentStep === 3 && (
            <div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: theme.colors.text.primary,
                marginBottom: '16px'
              }}>
                Smart Contract Configuration
              </h2>
              <p style={{
                fontSize: '14px',
                color: theme.colors.text.secondary,
                marginBottom: '24px'
              }}>
                Configure the smart contract connected to this Kickback using a supported template.
              </p>

              {/* Batch 1B canonical repair — the wizard now owns the
                  Existing Contract Draft banner internally so the banner
                  and the configuration form derive from the SAME
                  normalized draft object (Section D1 canonical rule).
                  The parent no longer renders duplicate banners. */}
              <SmartContractWizard
                onComplete={(contract) => {
                  updateField('contract', contract);
                  // Objective E — surface a clear "Contract Draft Ready"
                  // confirmation before advancing so the Creator sees the
                  // action succeeded rather than being jump-cut to Step 4.
                  if (contract && !contract.existing) {
                    setTimeout(() => setCurrentStep((s) => Math.min(5, s + 1)), 1500);
                  }
                }}
                portfolioType={formData.portfolioTypes[0]}
                portfolioId={editingId || pendingPortfolioId || null}
                initialDraftId={formData.contract?.contract_id || null}
                initialTemplateType={formData.contract?.template_type || null}
                initialContractData={formData.contract?.contract_data || null}
              />
            </div>
          )}

          {/* STEP 4: Management & Details */}
          {currentStep === 4 && (
            <div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: theme.colors.text.primary,
                marginBottom: '24px'
              }}>
                Portfolio Management & Terms
              </h2>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  marginBottom: '12px'
                }}>
                  Management Type *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <button
                    onClick={() => updateField('managementType', 'diy')}
                    style={{
                      padding: '20px',
                      border: `2px solid ${formData.managementType === 'diy' ? theme.colors.primary : theme.colors.borders}`,
                      borderRadius: theme.radius.md,
                      background: formData.managementType === 'diy' ? `${theme.colors.primary}15` : '#FFFFFF',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                      DIY / Self-Managed
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
                      You manage all aspects of your portfolio
                    </div>
                  </button>

                  <button
                    onClick={() => updateField('managementType', 'broker')}
                    style={{
                      padding: '20px',
                      border: `2px solid ${formData.managementType === 'broker' ? theme.colors.primary : theme.colors.borders}`,
                      borderRadius: theme.radius.md,
                      background: formData.managementType === 'broker' ? `${theme.colors.primary}15` : '#FFFFFF',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>
                      Kickback Architect Managed
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.text.secondary }}>
                      An approved Kickback Architect can help review, structure, or support this Kickback setup.
                    </div>
                  </button>
                </div>
              </div>

              {formData.managementType === 'broker' && (
                <div style={{
                  marginBottom: '24px',
                  padding: '16px',
                  background: '#FFF9E6',
                  border: '1px solid #FFD700',
                  borderRadius: theme.radius.sm
                }}>
                  <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.6' }}>
                    <strong>Kickback Architect Selection:</strong> Kickback Architect selection will be available in the next step. Approved Kickback Architects are reviewed before being listed. Availability may vary.
                  </p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    marginBottom: '8px'
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      Primary-Sale Kickback Contribution * (%)
                      <HelpTip
                        testId="help-primary-sale"
                        title="Primary-Sale Kickback Contribution"
                        content={"Percentage of each qualifying primary sale designated for this Kickback program. Separate from the secondary-sale Royalty % configured on the smart contract. Advanced economic-participation features (revenue-sharing, distributions, custody, etc.) may depend on platform availability, eligibility, additional agreements/providers and applicable requirements. Entering a value here does not imply those systems are already active."}
                      />
                    </span>
                  </label>
                  <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 0, marginBottom: 6, lineHeight: 1.5 }}>
                    Percentage of each qualifying primary sale designated for this Kickback program.
                  </p>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={formData.kickbackPercent}
                    onChange={(e) => updateField('kickbackPercent', parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.colors.borders}`,
                      borderRadius: theme.radius.sm,
                      fontSize: '14px',
                      color: theme.colors.text.primary,
                      background: '#FFFFFF'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    marginBottom: '8px'
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      Total Kickbacks in Portfolio *
                      <HelpTip
                        testId="help-total-kickbacks"
                        title="Total Kickbacks in Portfolio"
                        content={"Canonical total supply for this Kickback. For ERC-1155 contracts this is derived automatically from Number of Token IDs × Max Supply Per ID configured in Step 3 and cannot be edited here — this prevents the portfolio quantity from drifting from the smart-contract supply."}
                      />
                    </span>
                  </label>
                  {(() => {
                    const tt = (formData.contract && formData.contract.template_type) ? String(formData.contract.template_type).toUpperCase() : '';
                    const cd = formData.contract?.contract_data || formData.contract?.params || {};
                    const derived = tt.includes('1155')
                      ? (Number(cd.total_supply)
                          || (Number(cd.token_ids || 0) * Number(cd.max_supply_per_id || 0))
                          || 0)
                      : null;
                    if (derived && derived > 0) {
                      return (
                        <div>
                          <div
                            data-testid="quantity-derived-readonly"
                            style={{
                              padding: '12px',
                              border: `1px solid ${theme.colors.borders}`,
                              borderRadius: theme.radius.sm,
                              background: theme.colors.bg.secondary,
                              fontSize: 14,
                              fontWeight: 600,
                              color: theme.colors.text.primary,
                              cursor: 'not-allowed',
                            }}
                            aria-readonly="true"
                          >
                            {derived.toLocaleString()}
                          </div>
                          <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 6, lineHeight: 1.5 }}>
                            Derived from Step 3 (Number of Token IDs × Max Supply Per ID). Adjust it in the Smart Contract step.
                          </p>
                        </div>
                      );
                    }
                    return (
                      <input
                        type="number"
                        min={1}
                        value={formData.nftQuantity}
                        onChange={(e) => {
                          const q = parseInt(e.target.value) || 0;
                          updateField('nftQuantity', q);
                          setFormData(prev => {
                            if (!prev.contract) return prev;
                            const params = { ...(prev.contract.params || {}), max_supply: String(q) };
                            return { ...prev, contract: { ...prev.contract, params } };
                          });
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: `1px solid ${theme.colors.borders}`,
                          borderRadius: theme.radius.sm,
                          fontSize: '14px',
                          color: theme.colors.text.primary,
                          background: '#FFFFFF'
                        }}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Review & Submit */}
          {currentStep === 5 && (
            <div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: theme.colors.text.primary,
                marginBottom: '8px'
              }}>
                Review &amp; Save Kickback
              </h2>
              <p style={{ fontSize: '13px', color: theme.colors.text.secondary, marginBottom: '24px', lineHeight: 1.55 }}>
                Review the information you entered before saving this Kickback. Some Kickbacks may still require contract deployment, Kickback Architect review, additional evidence, utilities, payout setup, or other launch requirements before they can go live.
              </p>

              {/* Summary */}
              <div style={{
                background: theme.colors.bg.secondary,
                padding: '24px',
                borderRadius: theme.radius.md,
                marginBottom: '24px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '16px' }}>
                  Kickback Summary
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Name: </span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {formData.name}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Category: </span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {formData.category === 'Other' ? formData.otherCategory : formData.category}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Kickback Types: </span>
                    <span data-testid="summary-kickback-types" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {(() => {
                        const MAP = {
                          revenue_sharing: 'Revenue Sharing',
                          product_utility: 'Product Utility',
                          merch: 'Merchandise',
                          merchandise: 'Merchandise',
                          drawings_reward: 'Drawings & Reward',
                          event_pass: 'Event Pass / Ticket',
                          subscription_utility: 'Subscription Utility',
                          collectible: 'Collectible',
                          service_utility: 'Service Utility',
                          membership: 'Membership',
                          digital_collectible: 'Digital Collectible',
                          kickback_architect_managed: 'Kickback Architect Managed',
                        };
                        const pretty = (v) => MAP[String(v).toLowerCase()] || String(v)
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (c) => c.toUpperCase());
                        return (formData.portfolioTypes || []).map(pretty).join(', ') || '—';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Images Selected: </span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {formData.selectedNFTs.length}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Contract Standard: </span>
                    <span data-testid="summary-contract-standard" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {(() => {
                        const t = formData.contract?.template_type;
                        if (!t) return 'Not selected';
                        if (typeof t === 'string' && t.toLowerCase().includes('erc-721')) return 'ERC-721';
                        if (typeof t === 'string' && t.toLowerCase().includes('erc721')) return 'ERC-721';
                        if (typeof t === 'string' && t.toLowerCase().includes('erc-1155')) return 'ERC-1155';
                        if (typeof t === 'string' && t.toLowerCase().includes('erc1155')) return 'ERC-1155';
                        if (typeof t === 'string' && t.toLowerCase().includes('erc-20')) return 'ERC-20';
                        return String(t);
                      })()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Canonical Total Supply: </span>
                    <span data-testid="summary-total-supply" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {(() => {
                        const p = formData.contract?.contract_data || formData.contract?.params || {};
                        return p.total_supply || (Number(p.token_ids || 0) * Number(p.max_supply_per_id || 0)) || formData.nftQuantity || '—';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Secondary-Sale Royalty: </span>
                    <span data-testid="summary-royalty" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {(() => {
                        const p = formData.contract?.contract_data || formData.contract?.params || {};
                        return p.royalty_percent !== undefined && p.royalty_percent !== '' && p.royalty_percent !== null
                          ? `${p.royalty_percent}%`
                          : '—';
                      })()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Primary-Sale Kickback Contribution: </span>
                    <span data-testid="summary-primary-contribution" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {formData.kickbackPercent ? `${formData.kickbackPercent}%` : '—'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Contract Draft: </span>
                    <span data-testid="summary-contract-status" style={{ fontSize: '14px', fontWeight: 600, color: formData.contract?.contract_id ? theme.colors.status.success : theme.colors.text.muted }}>
                      {formData.contract?.contract_id ? 'Created' : 'Not created yet'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Management: </span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {formData.managementType === 'diy' ? 'Self-Managed' : 'Kickback Architect Managed'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Agreement */}
              <div style={{
                padding: '20px',
                background: '#FFF3E0',
                border: '1px solid #FF9800',
                borderRadius: theme.radius.md,
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <AlertCircle size={20} style={{ color: '#F57C00', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#E65100', marginBottom: '8px' }}>
                      Important: Kickback Submission Agreement
                    </p>
                    <p style={{ fontSize: '13px', color: '#BF360C', lineHeight: '1.6', marginBottom: '12px' }}>
                      By saving this Kickback setup, you confirm that the information provided is accurate, that you have the rights or permission to use the uploaded artwork, product details, and related materials, and that you understand this Kickback may require review, approval, and additional setup before it can go live. Any holder benefits, utilities, or reward-related features must follow the rules and settings approved for this Kickback.
                    </p>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.agreementAccepted}
                        onChange={(e) => updateField('agreementAccepted', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', color: '#BF360C', fontWeight: 600 }}>
                        I accept the Kickback submission agreement.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Phase 7F — Safe language warnings (warn-not-block) shown after submit */}
          {safeWarnings.length > 0 && (
            <div data-testid="wizard-safe-language-block" style={{ marginTop: 24 }}>
              <SafeLanguageWarnings warnings={safeWarnings} />
              <button
                data-testid="wizard-continue-after-warnings"
                onClick={() => {
                  if (pendingPortfolioId) navigate(`/creator/portfolios/${pendingPortfolioId}`);
                  else navigate('/creator/portfolios');
                }}
                style={{
                  marginTop: 8, padding: '10px 20px', border: 'none',
                  borderRadius: theme.radius.sm, background: theme.colors.primary,
                  color: '#FFFFFF', fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}
              >
                Continue to Portfolio
              </button>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '40px',
            paddingTop: '24px',
            borderTop: `1px solid ${theme.colors.borders}`
          }}>
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: theme.radius.sm,
                background: '#FFFFFF',
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 1 ? 0.5 : 1
              }}
            >
              <ChevronLeft size={18} />
              Back
            </button>

            <button
              data-testid="wizard-continue-btn"
              onClick={handleNext}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 32px',
                border: 'none',
                borderRadius: theme.radius.sm,
                background: !loading ? theme.colors.primary : theme.colors.bg.tertiary,
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: !loading ? 'pointer' : 'not-allowed',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving…' : currentStep === 5 ? 'Save Kickback' : 'Continue'}
              {!loading && currentStep < 5 && <ChevronRight size={18} />}
              {!loading && currentStep === 5 && <Check size={18} />}
            </button>
          </div>
        </div>
      </div>
      {/* Batch 1C-A correction — "Draft Saved" confirmation after
          Save and Finish Later. Creator may resume immediately or
          jump to the Kickback detail page. */}
      {showDraftSaved && (
        <div
          data-testid="wizard-draft-saved-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div style={{ background: '#FFFFFF', borderRadius: theme.radius.md, padding: 28, maxWidth: 480, width: '92%', boxShadow: theme.colors.shadows.lg }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Check size={22} style={{ color: theme.colors.status.success }} />
              <h3 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text.primary }}>Draft Saved</h3>
            </div>
            <p style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.6, marginBottom: 20 }}>
              Your Kickback setup has been saved. You can resume from where you left off whenever you are ready.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                data-testid="draft-saved-resume-btn"
                onClick={() => setShowDraftSaved(null)}
                style={{ padding: '10px 16px', border: 'none', background: theme.colors.primary, color: '#FFF', borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Resume Setup
              </button>
              <button
                data-testid="draft-saved-view-btn"
                onClick={() => navigate(`/creator/portfolios/${showDraftSaved}?draft_saved=1`)}
                style={{ padding: '10px 16px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                View Kickback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch 1C-A — Exit confirmation modal (only when dirty). */}
      {showExitConfirm && (
        <div
          data-testid="wizard-exit-confirm-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowExitConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFFFF', borderRadius: theme.radius.md, padding: 24,
              maxWidth: 460, width: '92%', boxShadow: theme.colors.shadows.lg,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 8 }}>
              {dirty ? 'Leave Without Saving?' : 'Exit setup?'}
            </h3>
            <p style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.6, marginBottom: 20 }}>
              {dirty
                ? 'You have unsaved changes. Save your draft before leaving so you can continue later.'
                : 'You can return and continue this Kickback setup at any time.'}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                data-testid="wizard-exit-cancel-btn"
                onClick={() => setShowExitConfirm(false)}
                style={{ padding: '8px 14px', border: `1px solid ${theme.colors.borders}`, background: '#FFFFFF', borderRadius: theme.radius.sm, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}
              >
                Cancel
              </button>
              {dirty && (
                <button
                  data-testid="wizard-exit-save-later-btn"
                  onClick={async () => { setShowExitConfirm(false); await handleSaveAndFinishLater(); }}
                  style={{ padding: '8px 14px', border: 'none', background: theme.colors.primary, color: '#FFFFFF', borderRadius: theme.radius.sm, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  Save and Finish Later
                </button>
              )}
              <button
                data-testid="wizard-exit-leave-btn"
                onClick={() => { setShowExitConfirm(false); navigate('/creator/dashboard'); }}
                style={{ padding: '8px 14px', border: `1px solid ${theme.colors.status.error}`, background: '#FFFFFF', color: theme.colors.status.error, borderRadius: theme.radius.sm, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                Leave Without Saving
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch 1C-A — Save Kickback Setup success screen (final Step 5
          save). Replaces the immediate redirect so the Creator sees a
          clear confirmation and next actions. */}
      {showSavedSuccess && (
        <div
          data-testid="wizard-saved-success-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div style={{
            background: '#FFFFFF', borderRadius: theme.radius.md, padding: 32,
            maxWidth: 520, width: '92%', boxShadow: theme.colors.shadows.lg, textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Check size={24} style={{ color: theme.colors.status.success }} />
              <h3 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text.primary }}>
                Kickback Saved
              </h3>
            </div>
            <p style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.6, marginBottom: 22 }}>
              Your Kickback has been saved successfully. Review the remaining launch requirements before making it available to holders.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                data-testid="wizard-saved-view-btn"
                onClick={() => navigate(`/creator/portfolios/${showSavedSuccess}`)}
                style={{ padding: '10px 16px', border: 'none', background: theme.colors.primary, color: '#FFF', borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                View Kickback
              </button>
              <button
                data-testid="wizard-saved-launch-readiness-btn"
                onClick={() => navigate(`/creator/portfolios/${showSavedSuccess}/launch-readiness`)}
                style={{ padding: '10px 16px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Review Launch Readiness
              </button>
              <button
                data-testid="wizard-saved-create-another-btn"
                onClick={() => window.location.assign('/creator/portfolios/new')}
                style={{ padding: '10px 16px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Create Another Kickback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch 1C-B1 FINAL Part 3 — branded finalize error modal replaces
          the previous `window.alert('initial_setup_incomplete')` failure
          path. Missing items come from the canonical readiness response. */}
      {finalizeErrorModal && (
        <div data-testid="finalize-error-modal-outer" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#FFFFFF', borderRadius: theme.radius.md, padding: 32, maxWidth: 520, width: '92%', boxShadow: theme.colors.shadows.lg }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <AlertCircle size={22} style={{ color: '#B45309' }} />
              <h3 style={{ fontSize: 20, fontWeight: 700, color: theme.colors.text.primary }}>{finalizeErrorModal.title}</h3>
            </div>
            <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 16 }}>{finalizeErrorModal.body}</p>
            <ul style={{ paddingLeft: 20, margin: '0 0 20px', fontSize: 13, color: theme.colors.text.primary }}>
              {finalizeErrorModal.blockers.map((b, i) => <li key={i} data-testid={`finalize-modal-blocker-${i}`}>{b}</li>)}
            </ul>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button data-testid="finalize-modal-review-btn"
                onClick={() => { setFinalizeErrorModal(null); if (editingId) navigate(`/creator/portfolios/${editingId}/launch-readiness`); }}
                style={{ padding: '10px 16px', border: 'none', background: theme.colors.primary, color: '#FFF', borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Review Missing Items
              </button>
              <button data-testid="finalize-modal-return-btn"
                onClick={() => setFinalizeErrorModal(null)}
                style={{ padding: '10px 16px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Return to Setup
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EnhancedPortfolioWizard;
